/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosRichTextProps } from "../rich-text";
import { CosmosTextProps, getPlainText } from "./CosmosText.atom";
import { CosmosTextAs, CosmosTextVariants, VariantType, CosmosVariableRichText } from "./text.types";

/**
 * Checks if a CosmosVariableRichText has actual content (not just empty HTML tags or whitespace).
 * This utility helps prevent rendering empty elements when content is set to values like `<p></p>`.
 *
 * @param content - The CosmosVariableRichText object to check, or a plain string (for backward compatibility)
 * @returns `true` if the content has meaningful text, `false` otherwise
 *
 * @example
 * ```ts
 * hasContent({ text: "Hello" }) // true
 * hasContent({ html: "<p></p>" }) // false
 * hasContent({ html: "<p>Hello</p>" }) // true
 * hasContent(null) // false
 * hasContent("<p></p>") // false (for string compatibility)
 * ```
 */
export const hasContent = (content: CosmosVariableRichText | string | null | undefined): boolean => {
	if (!content) return false;

	// Handle string input (for backward compatibility with eyebrow field)
	if (typeof content === "string") {
		return getPlainText(content).length > 0;
	}

	// Handle CosmosVariableRichText object
	const hasTextContent = content.text ? content.text.trim().length > 0 : false;
	const hasHtmlContent = content.html ? getPlainText(content.html).length > 0 : false;

	return hasTextContent || hasHtmlContent;
};

export const getTextAs = (props: CosmosTextProps | CosmosRichTextProps): CosmosTextAs | null | undefined => {
	let as = props.as;
	if (!props.as && props.children) {
		if (props.variant?.startsWith("paragraph")) {
			as = "div";
		}
		if (props.variant === "heading2XL") {
			as = "h1";
		}
		if (props.variant === "headingXL") {
			as = "h2";
		}
		if (props.variant === "headingLG") {
			as = "h3";
		}
		if (props.variant === "headingMD") {
			as = "h4";
		}
		if (props.variant === "headingSM") {
			as = "h5";
		}
		if (props.variant === "eyebrowLG") {
			as = "h6";
		}
	}

	return as;
};

/**
 * Shift a heading tag (`h1`–`h6`) by `offset`, clamped to `h2`–`h6`. Non-heading
 * tags (`div`, `span`, `p`) and `null`/`undefined` pass through unchanged.
 *
 * Floored at `h2`: a shifted heading can never become an `h1` — that's reserved
 * for the page hero, so a content region shifted up one level won't introduce a
 * second top-level heading. Used by CosmosText / CosmosRichText to honor
 * `HeadingLevelContext`.
 */
export const shiftHeadingTag = (
	as: CosmosTextAs | null | undefined,
	offset: number,
): CosmosTextAs | null | undefined => {
	if (!offset || !as) return as;
	const match = /^h([1-6])$/.exec(as);
	if (!match) return as;
	const level = Math.min(6, Math.max(2, Number(match[1]) + offset));
	return `h${level}` as CosmosTextAs;
};

/**
 * Normalize heading levels in authored rich-text HTML so its shallowest heading
 * sits at `baseLevel` (default 2 — one below the page hero's h1), preserving the
 * relative structure below it. Applied to CMS body content where editors often
 * start sections at "Heading 3", which — under the hero h1 — reads as an h1→h3
 * SKIPPED_LEVEL.
 *
 * Non-destructive: it adds `aria-level` rather than rewriting tags, so the
 * visual styling (which is tag-driven in the article body) is untouched. Assistive
 * tech and the heading auditor honor `aria-level` over the tag's implicit level.
 * Returns the html unchanged when there are no headings or no shift is needed.
 */
export const normalizeRichTextHeadingLevels = (html?: string | null, baseLevel = 2): string | null | undefined => {
	if (!html) return html;
	const levels: number[] = [];
	const scan = /<h([1-6])[\s>]/gi;
	let match: RegExpExecArray | null;
	while ((match = scan.exec(html)) !== null) levels.push(Number(match[1]));
	if (levels.length === 0) return html;

	const shift = baseLevel - Math.min(...levels);
	if (shift === 0) return html;

	return html.replace(/<h([1-6])([^>]*)>/gi, (full, digit: string, attrs: string) => {
		const level = Number(digit);
		const newLevel = Math.min(6, Math.max(1, level + shift));
		if (newLevel === level || /\baria-level\s*=/i.test(attrs)) return full;
		return `<h${digit}${attrs} aria-level="${newLevel}">`;
	});
};

/**
 * Close skipped-level *gaps* in authored rich-text (e.g. legal/EULA HTML that jumps
 * h1 → h3, skipping h2) and, optionally, re-base its shallowest heading. Unlike
 * {@link normalizeRichTextHeadingLevels} (a uniform shift), this walks the heading
 * outline and renumbers each heading to at most `parent + 1`, so an
 * `h1, h3, h3` document becomes `h1, h2, h2` — no jump greater than one anywhere.
 *
 * `baseLevel` sets the level of the shallowest heading: omit it to KEEP the authored
 * top level (so the document's own top heading — often the page h1 — is preserved);
 * pass `2` when a hero owns the page h1 above this content, which demotes the authored
 * top heading to h2 (collapsing a duplicate <h1> to an effective h2) while still
 * closing internal gaps.
 *
 * Non-destructive: adds `aria-level` rather than rewriting tags, so tag-driven visual
 * styling is untouched; assistive tech and the heading auditor honor `aria-level`.
 * Returns the html unchanged when it has no headings or no change is needed.
 */
export const closeRichTextHeadingGaps = (html?: string | null, baseLevel?: number): string | null | undefined => {
	if (!html) return html;
	const authored: number[] = [];
	const scan = /<h([1-6])[\s>]/gi;
	let match: RegExpExecArray | null;
	while ((match = scan.exec(html)) !== null) authored.push(Number(match[1]));
	if (authored.length === 0) return html;

	const start = baseLevel ?? Math.min(...authored);
	const effective: number[] = [];
	const stack: { authored: number; level: number }[] = [];
	for (const a of authored) {
		while (stack.length && stack[stack.length - 1].authored >= a) stack.pop();
		const level = stack.length ? Math.min(6, stack[stack.length - 1].level + 1) : start;
		stack.push({ authored: a, level });
		effective.push(level);
	}
	if (effective.every((level, i) => level === authored[i])) return html;

	let i = 0;
	return html.replace(/<h([1-6])([^>]*)>/gi, (full, digit: string, attrs: string) => {
		const level = effective[i++];
		if (level === Number(digit) || /\baria-level\s*=/i.test(attrs)) return full;
		return `<h${digit}${attrs} aria-level="${level}">`;
	});
};

/**
 * Like {@link closeRichTextHeadingGaps}, but treats an ORDERED list of html blocks as ONE
 * continuous heading outline. A legal doc is often authored across several ckText blocks
 * (e.g. the title in one block, the numbered clauses in another) — closing gaps per-block
 * can't bridge a block-1 <h1> → block-2 <h3> jump. This walks all blocks with a shared
 * stack so the whole document nests without skips.
 *
 * `null`/`undefined` entries (non-heading sections) pass through in place, keeping the
 * returned array index-aligned with the input. `baseLevel` behaves as in
 * closeRichTextHeadingGaps (omit to keep the document's shallowest authored level; pass 2
 * when a hero owns the page h1). Non-destructive: adds `aria-level`, never rewrites tags.
 */
export const closeHeadingGapsAcrossBlocks = (
	htmls: (string | null | undefined)[],
	baseLevel?: number,
): (string | null | undefined)[] => {
	const perBlockAuthored: number[][] = [];
	let globalMin = 7;
	for (const html of htmls) {
		const levels: number[] = [];
		if (html) {
			const scan = /<h([1-6])[\s>]/gi;
			let match: RegExpExecArray | null;
			while ((match = scan.exec(html)) !== null) {
				const n = Number(match[1]);
				levels.push(n);
				globalMin = Math.min(globalMin, n);
			}
		}
		perBlockAuthored.push(levels);
	}
	if (globalMin === 7) return htmls; // no headings in any block

	const start = baseLevel ?? globalMin;
	const stack: { authored: number; level: number }[] = [];
	const perBlockEffective = perBlockAuthored.map((levels) =>
		levels.map((a) => {
			while (stack.length && stack[stack.length - 1].authored >= a) stack.pop();
			const level = stack.length ? Math.min(6, stack[stack.length - 1].level + 1) : start;
			stack.push({ authored: a, level });
			return level;
		}),
	);

	return htmls.map((html, blockIndex) => {
		if (!html) return html;
		const effective = perBlockEffective[blockIndex];
		const authored = perBlockAuthored[blockIndex];
		if (effective.every((level, i) => level === authored[i])) return html;
		let i = 0;
		return html.replace(/<h([1-6])([^>]*)>/gi, (full, digit: string, attrs: string) => {
			const level = effective[i++];
			if (level === Number(digit) || /\baria-level\s*=/i.test(attrs)) return full;
			return `<h${digit}${attrs} aria-level="${level}">`;
		});
	});
};

export const getTextVariantType = (variant?: CosmosTextVariants | null): VariantType => {
	let variantType: VariantType = "heading";

	if (variant?.includes("paragraph")) {
		variantType = "paragraph";
	} else if (variant?.includes("ui")) {
		variantType = "ui";
	} else if (variant?.includes("eyebrow")) {
		variantType = "eyebrow";
	}

	return variantType;
};
